// ============================================================================
// GumaHUB Alert Component - Enterprise Design
// ============================================================================

import React from 'react';
import { cn } from '../utils/index.js';

export interface AlertProps {
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  action?: React.ReactNode;
}

const typeStyles: Record<string, {
  container: string;
  icon: string;
  iconBg: string;
  title: string;
}> = {
  info: {
    container: 'bg-ocean-50 dark:bg-[#8ab4f8]/10 border-ocean-200 dark:border-[#8ab4f8]/30',
    icon: 'text-ocean-600 dark:text-[#8ab4f8]',
    iconBg: 'bg-ocean-100 dark:bg-[#8ab4f8]/20',
    title: 'text-ocean-800 dark:text-[#8ab4f8]',
  },
  success: {
    container: 'bg-mint-50 dark:bg-[#81c995]/10 border-mint-200 dark:border-[#81c995]/30',
    icon: 'text-mint-600 dark:text-[#81c995]',
    iconBg: 'bg-mint-100 dark:bg-[#81c995]/20',
    title: 'text-mint-800 dark:text-[#81c995]',
  },
  warning: {
    container: 'bg-amber-50 dark:bg-[#fdd663]/10 border-amber-200 dark:border-[#fdd663]/30',
    icon: 'text-amber-600 dark:text-[#fdd663]',
    iconBg: 'bg-amber-100 dark:bg-[#fdd663]/20',
    title: 'text-amber-800 dark:text-[#fdd663]',
  },
  error: {
    container: 'bg-red-50 dark:bg-[#ea8685]/10 border-guma-200 dark:border-[#ea8685]/30',
    icon: 'text-guma-600 dark:text-[#ea8685]',
    iconBg: 'bg-guma-100 dark:bg-[#ea8685]/20',
    title: 'text-guma-800 dark:text-[#ea8685]',
  },
};

const icons: Record<string, React.ReactNode> = {
  info: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  success: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

export const Alert: React.FC<AlertProps> = ({
  type = 'info',
  title,
  children,
  onClose,
  className,
  action,
}) => {
  const styles = typeStyles[type];

  return (
    <div
      className={cn(
        'rounded-xl border p-4',
        styles.container,
        className
      )}
      role="alert"
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={cn('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', styles.iconBg, styles.icon)}>
          {icons[type]}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={cn('text-sm font-semibold mb-1', styles.title)}>
              {title}
            </h4>
          )}
          <div className={cn('text-sm', styles.title, 'opacity-80')}>
            {children}
          </div>

          {/* Action */}
          {action && (
            <div className="mt-3">
              {action}
            </div>
          )}
        </div>

        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className={cn(
              'flex-shrink-0 p-1 rounded-lg transition-colors',
              styles.icon,
              'hover:bg-white/50'
            )}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// Toast variant for notifications
export interface ToastProps {
  type?: 'info' | 'success' | 'warning' | 'error';
  message: string;
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  type = 'info',
  message,
  onClose,
}) => {
  const styles = typeStyles[type];

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border animate-fade-in-up',
        'bg-white dark:bg-[#2d2d2d]',
        styles.container.replace('bg-', 'border-')
      )}
      role="alert"
    >
      <div className={cn('flex-shrink-0', styles.icon)}>
        {icons[type]}
      </div>
      <p className="text-sm font-medium text-slate-900 dark:text-[#e8eaed]">{message}</p>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 text-slate-400 dark:text-[#9aa0a6] hover:text-slate-600 dark:hover:text-[#e8eaed] rounded transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Alert;
